/**
 * ExtractedResumePage — jobs matched to an uploaded resume (/search_by_resume).
 * Mirrors Flutter ExtractedResumeView (lib/presentation/web/extracted_resume/extracted_resume_view.dart):
 * the resume's skills (tap to add to the search), the search bar, filters + sort and the job list.
 * Logic lives in useExtractedResume; the search card, list and job preview are the Jobs page's.
 * Below the split layout each card carries Flutter's Apply / Easy Apply / Applied actions.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '@/components/layout/PageLayout';
import { useTenantStore } from '@/store/tenantStore';
import { JobSearchBar } from '@/features/home/components/JobSearchBar';
import { HomeJobListItem } from '@/features/home/components/HomeJobListItem';
import { HomeJobDetailPanel } from '@/features/home/components/HomeJobDetailPanel';
import { useIsSplitLayout } from '@/hooks/useMediaQuery';
import { Toast } from '@/features/home/components/Toast';
import type { JobEntity } from '@/types/job.types';
import { FilterDropdown, SortDropdown } from './components/JobFilters';
import { SkillsIcon } from './components/JobCardParts';
import { getSupportedFilterKeys } from './utils/filterState';
import { useExtractedResume, MAX_RESUME_SKILLS_SHOWN } from './hooks/useExtractedResume';
import type { ExtractedResume } from './hooks/useExtractedResume';
import { JobListShimmer, NoMatchWidget } from './JobsPage';
import jobStyles from './JobsPage.module.css';
import styles from './ExtractedResumePage.module.css';

const SHIMMER_JOBS = 4;

export function ExtractedResumePage() {
  const { t } = useTranslation();
  const { state, actions } = useExtractedResume();
  const isSplit = useIsSplitLayout();
  const preferences = useTenantStore((s) => s.preferences);
  const enableEasyApply = !!(useTenantStore((s) => s.config) as Record<string, unknown> | null)
    ?.enableEasyApply;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { resume, entities, aggregations, isLoading, isLoadingMore, totalCount } = state;
  const hasResults = entities.length > 0;
  // Flutter shows filters and sort only when there are results
  const showResults = aggregations.length > 0 && hasResults;
  const showNoMatch = !isLoading && !hasResults;

  const supportedKeys = getSupportedFilterKeys(preferences as Record<string, unknown> | null);
  const filterAggregations = aggregations.filter((a) => supportedKeys.includes(a.name ?? ''));
  const sortAggregation = aggregations.find((a) => a.name === 'sortBy');

  const selectedIndex = Math.max(
    0,
    entities.findIndex((e) => (e.displayId ?? e.id) === selectedId),
  );
  const selected = entities[selectedIndex];

  const selectJob = (entity: JobEntity) => {
    if (!isSplit) {
      actions.openJob(entity);
      return;
    }
    setSelectedId(entity.displayId ?? entity.id ?? null);
  };

  return (
    <PageLayout>
      <div className={jobStyles.container}>
        <div className={jobStyles.sectionHeading}>
          <span className={jobStyles.accent} />
          <div>
            <h1 className={jobStyles.sectionTitle}>
              {t('Elevate Your Job Search')}{' '}
              <span className={jobStyles.titleHighlight}>{t(' With AI').trim()}</span>
            </h1>
          </div>
        </div>

        {resume?.showDetails && (
          <ResumeCard
            resume={resume}
            barSkills={state.barSkills}
            onAddSkill={actions.addResumeSkill}
            onDelete={() => setConfirmDelete(true)}
          />
        )}

        <div className={jobStyles.searchCard}>
          <JobSearchBar
            searchInput={state.searchInput}
            onSearch={actions.search}
            variant="inline"
            showSwitch
            skills={state.barSkills}
            onSkillsChange={actions.setBarSkills}
          />

          {showResults && (
            <div className={jobStyles.filterRow}>
              <span className={jobStyles.filterLabel}>{t('Filters:')}</span>
              <div className={jobStyles.filterChips}>
                {filterAggregations.map((agg) => (
                  <FilterDropdown
                    key={agg.name}
                    aggregation={agg}
                    onChange={actions.onFilterSelection}
                    onApply={actions.applyFilters}
                    onClear={actions.clearFilter}
                  />
                ))}
              </div>
              <div className={jobStyles.filterActions}>
                {sortAggregation && <SortDropdown aggregation={sortAggregation} onSelect={actions.selectSort} />}
                <button className={jobStyles.filterActionBtn} onClick={actions.clearFilters} type="button">
                  {t('Clear all')}
                </button>
                <button
                  className={`${jobStyles.filterActionBtn} ${jobStyles.filterApply}`}
                  onClick={actions.applyFilters}
                  type="button"
                >
                  {t('Apply Filters')}
                </button>
              </div>
            </div>
          )}
        </div>

        {showResults && (
          <div className={jobStyles.toolbar}>
            <div className={jobStyles.toolbarTitle}>
              <h2 className={jobStyles.resultsTitle}>
                {state.isResumeSkills ? t('Job matches for your Resume') : t('Job Matches')}
              </h2>
            </div>
            {/* Flutter: '${Showing} ${total} ${Matching Job}' */}
            <p className={jobStyles.count}>
              <strong>
                {t('Showing')} {totalCount} {t('Matching Job')}
              </strong>
            </p>
          </div>
        )}

        {showNoMatch ? (
          <NoMatchWidget />
        ) : (
          <div className={jobStyles.split}>
            <div className={jobStyles.listCol}>
              <div className={jobStyles.list}>
                {isLoading ? (
                  <JobListShimmer count={SHIMMER_JOBS} />
                ) : (
                  <>
                    {entities.map((entity, index) => (
                      <HomeJobListItem
                        key={entity.displayId ?? entity.id ?? index}
                        entity={entity}
                        isSelected={isSplit && index === selectedIndex}
                        onSelect={() => selectJob(entity)}
                        onSave={() => actions.saveJob(entity, index)}
                        onUnsave={() => actions.unsaveJob(entity, index)}
                        enableEasyApply={enableEasyApply}
                        onApply={(isEasyApply) => actions.applyToJob(entity, isEasyApply)}
                      />
                    ))}
                    {isLoadingMore && <JobListShimmer count={2} />}
                  </>
                )}
              </div>
            </div>

            {isSplit && (
              <div className={jobStyles.detailCol}>
                {isLoading ? (
                  <div className={jobStyles.shimmerDetail} aria-hidden="true" />
                ) : selected ? (
                  <HomeJobDetailPanel
                    key={selected.displayId ?? selected.id}
                    entity={selected}
                    onSave={() => actions.saveJob(selected, selectedIndex)}
                    onUnsave={() => actions.unsaveJob(selected, selectedIndex)}
                    onClickApply={(isEasyApply) => actions.applyToJob(selected, isEasyApply)}
                    onViewDetails={() => actions.openJob(selected)}
                  />
                ) : null}
              </div>
            )}
          </div>
        )}

        {confirmDelete && (
          <DeleteResumeDialog onCancel={() => setConfirmDelete(false)} onConfirm={actions.deleteResume} />
        )}

        {state.toast && <Toast message={state.toast.message} type={state.toast.type} onClose={actions.clearToast} />}
      </div>
    </PageLayout>
  );
}

/* ────────────────────────────── Resume card ────────────────────────────── */

/**
 * Flutter ExtractedResumeContainer — file name, delete, and up to 20 resume skills.
 * A skill already in the search bar is marked and can't be added again.
 */
function ResumeCard({
  resume,
  barSkills,
  onAddSkill,
  onDelete,
}: {
  resume: ExtractedResume;
  barSkills: string[];
  onAddSkill: (skill: string) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  // Flutter keeps the extension visible: long names lose the middle, not the last 8 characters
  const name = resume.fileName;
  const tail = name.length > 25 ? name.slice(-8) : '';
  const head = tail ? name.slice(0, -8) : name;
  const skills = resume.skills.slice(0, MAX_RESUME_SKILLS_SHOWN);

  return (
    <section className={styles.card} aria-labelledby="resume-skills-title">
      <div className={styles.fileRow}>
        <span className={styles.fileIcon} aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11.5 2.5H6a1.5 1.5 0 0 0-1.5 1.5v12A1.5 1.5 0 0 0 6 17.5h8a1.5 1.5 0 0 0 1.5-1.5V6.5l-4-4Z" />
            <path d="M11.5 2.5v4h4M7.5 11h5M7.5 14h3.5" />
          </svg>
        </span>
        <div className={styles.fileText}>
          <span className={styles.eyebrow}>{t('Extracted from your resume')}</span>
          {name && (
            <span className={styles.fileName} title={name}>
              <span className={styles.fileHead}>{head}</span>
              {tail && <span className={styles.fileTail}>{tail}</span>}
            </span>
          )}
        </div>
        <button type="button" className={styles.deleteBtn} onClick={onDelete} title={t('Delete Resume')} aria-label={t('Delete Resume')}>
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3.5 5.5h13M8 5.5V4h4v1.5M5.5 5.5l.7 10.2a1 1 0 0 0 1 .8h5.6a1 1 0 0 0 1-.8l.7-10.2" />
          </svg>
        </button>
      </div>

      <div className={styles.skillsHead}>
        <SkillsIcon />
        <h2 id="resume-skills-title" className={styles.skillsTitle}>{t('Skills')}</h2>
        <span className={styles.skillsHint}>{t('click skills to add it to the search criteria')}</span>
      </div>

      {/* Flutter renders an empty Wrap when the resume has no skills */}
      {skills.length > 0 && (
        <ul className={styles.skills}>
          {skills.map((skill) => {
            const added = barSkills.includes(skill);
            return (
              <li key={skill}>
                <button
                  type="button"
                  className={`${styles.skill} ${added ? styles.skillAdded : ''}`}
                  onClick={() => onAddSkill(skill)}
                  disabled={added}
                  aria-pressed={added}
                  title={skill}
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    {added ? <path d="m4.5 10.5 3.5 3.5 7.5-8" /> : <path d="M10 4.5v11M4.5 10h11" />}
                  </svg>
                  <span className={styles.skillLabel}>{skill}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** Flutter CustomDialog.confirmDialog — "Delete Resume". */
function DeleteResumeDialog({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={styles.backdrop} role="presentation" onClick={onCancel}>
      <div
        className={styles.dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-resume-title"
        aria-describedby="delete-resume-text"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.key === 'Escape' && onCancel()}
      >
        <h2 id="delete-resume-title" className={styles.dialogTitle}>{t('Delete Resume')}</h2>
        <p id="delete-resume-text" className={styles.dialogText}>
          {t('Are you sure, you want to delete the extracted resume ?')}
        </p>
        <div className={styles.dialogActions}>
          <button type="button" className={styles.cancelBtn} onClick={onCancel} autoFocus>
            {t('Cancel')}
          </button>
          <button type="button" className={styles.confirmBtn} onClick={onConfirm}>
            {t('Delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
